import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { sessionStats, heroContent } from '../mockData';
import { Card, CardContent } from './ui/card';
import { Clock, Award, GraduationCap } from 'lucide-react';
import { createStaggerContainer, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

// Calcule le nombre d'années depuis le 25 novembre 2020
const getYearsOfStudy = () => {
  const startDate = new Date(2020, 10, 25); // 25 novembre 2020 (mois commence à 0)
  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  // Si on n'a pas encore atteint le 25 novembre de cette année, on retire 1
  if (now < new Date(now.getFullYear(), 10, 25)) {
    years--;
  }
  return years;
};

const SessionSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.25 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.2, delayChildren: 0.1 });
  const yearsOfStudy = getYearsOfStudy();

  return (
    <motion.section id="seance" className="py-12 sm:py-16 lg:py-20 bg-[#F5F0E8]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.div className="flex items-center justify-center gap-3 mb-4" variants={fadeInUp}>
            <motion.span
              className="h-px w-12 bg-gradient-to-r from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#a5400e] font-medium">
              Séance
            </span>
            <motion.span
              className="h-px w-12 bg-gradient-to-l from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          </motion.div>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5c2810] mb-3 sm:mb-4 relative inline-block" variants={fadeInUp}>
            Votre séance de neuro-thérapie
            <motion.span
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#a5400e] to-transparent rounded-full"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#7a4a3a] max-w-3xl mx-auto px-4" variants={fadeInUp}>
            Un temps dédié pour comprendre vos besoins, relâcher les tensions et repartir avec des outils pour prolonger les bénéfices à la maison.
          </motion.p>
        </motion.div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12" variants={containerVariants}>
          {/* Tuile 1 - Durée */}
          <motion.div
            variants={scaleIn}
            whileHover={shouldReduceMotion ? undefined : { y: -8, transition: { duration: 0.3 } }}
            className="h-full"
          >
            <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.15)] transition-all duration-500">
              <CardContent className="p-6 sm:p-8 text-center h-full flex flex-col">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#a5400e]/10 flex items-center justify-center group-hover:bg-[#a5400e]/20 transition-colors">
                  <Clock className="text-[#a5400e]" size={28} />
                </div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#a5400e] mb-2">{sessionStats[0].value}</div>
                <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#a5400e]/30 to-transparent mx-auto mb-3" />
                <p className="text-sm text-[#7a4a3a] mt-auto">{sessionStats[0].label}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tuile 2 - Prise en charge */}
          <motion.div
            variants={scaleIn}
            whileHover={shouldReduceMotion ? undefined : { y: -8, transition: { duration: 0.3 } }}
            className="h-full"
          >
            <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.15)] transition-all duration-500">
              <CardContent className="p-6 sm:p-8 text-center h-full flex flex-col">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#a5400e]/10 flex items-center justify-center group-hover:bg-[#a5400e]/20 transition-colors">
                  <Award className="text-[#a5400e]" size={28} />
                </div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#a5400e] mb-2">{sessionStats[1].value}</div>
                <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#a5400e]/30 to-transparent mx-auto mb-3" />
                <p className="text-sm text-[#7a4a3a] mt-auto">{sessionStats[1].label}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tuile 3 - Formation */}
          <motion.div
            variants={scaleIn}
            whileHover={shouldReduceMotion ? undefined : { y: -8, transition: { duration: 0.3 } }}
            className="h-full"
          >
            <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.15)] transition-all duration-500">
              <CardContent className="p-6 sm:p-8 text-center h-full flex flex-col">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#a5400e]/10 flex items-center justify-center group-hover:bg-[#a5400e]/20 transition-colors">
                  <GraduationCap className="text-[#a5400e]" size={28} />
                </div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#a5400e] mb-1">{yearsOfStudy} ans</div>
                <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#a5400e]/30 to-transparent mx-auto mb-3" />
                <p className="text-sm text-[#7a4a3a] mt-auto">{sessionStats[2].label}</p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div className="relative bg-white/70 backdrop-blur-sm p-8 sm:p-10 lg:p-12 rounded-2xl border border-[#a5400e]/10 shadow-[0_20px_60px_rgb(165,64,14,0.08)] max-w-4xl mx-auto" variants={fadeInUp}>
          <div className="absolute -inset-1 bg-gradient-to-br from-[#a5400e]/5 via-[#d46a3e]/5 to-[#e8a87c]/5 rounded-2xl blur-2xl -z-10" />
          <p className="text-base sm:text-lg lg:text-xl font-serif text-[#5c2810] italic text-center leading-relaxed relative">
            "{heroContent.quote}"
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default SessionSection;
